import {
  AdminDollarInput2,
  Button,
  ChevronSmall,
  CollapsibleComponent,
  Disclaimer,
  Div,
  EditPenIconDarkGray,
  ErrorToast,
  Icon,
  P,
  PlusIcon,
  TextWithTooltipIcon,
  TrashIconRed,
  centsToDollars,
  formatter,
  lightTheme,
} from '@vgrubs/components';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { EarningPlanStyled, Pill } from './Earning.styled';
import { EarningPlanEntity } from 'components/src/types/location.types';
import {
  LocationAgentServiceKey,
  LocationAgentShare,
} from 'components/src/types/api/location.types';

const EarningPlanStatus = ({
  active,
  expired,
}: {
  active?: boolean;
  expired?: boolean;
}) => {
  if (active || expired) {
    return (
      <Pill $expired={expired}>
        <Disclaimer $m="0" $color={lightTheme.white}>
          {expired ? 'Expired' : 'Active'}
        </Disclaimer>
      </Pill>
    );
  } else return null;
};

// A plan box (the service card) whose content can expand to reveal the agents
// editor via a chevron. The collapsible auto-sizes to its content.
const PlanBox = ({
  expired,
  active,
  rowLeft,
  rowRight,
  agentsContent,
}: {
  expired?: boolean;
  active?: boolean;
  rowLeft: ReactNode;
  rowRight: ReactNode;
  agentsContent?: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const update = () => setHeight(el.scrollHeight + 10);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <EarningPlanStyled $expired={expired} $active={active}>
      <Div $flex $between $alignCenter $gap="8px" $gapSM="8px" $width="100%">
        {rowLeft}
        <Div $flex $alignCenter $gap="8px">
          {rowRight}
          {agentsContent && (
            <Icon
              $pointer
              $rotate={open ? '0deg' : '180deg'}
              onClick={() => setOpen((prev) => !prev)}
            >
              <ChevronSmall />
            </Icon>
          )}
        </Div>
      </Div>

      {agentsContent && (
        <CollapsibleComponent expanded={open} elementHeight={height}>
          <Div $mt="8" $p="0 1px" ref={contentRef}>
            {agentsContent}
          </Div>
        </CollapsibleComponent>
      )}
    </EarningPlanStyled>
  );
};

export const EarningPlan = ({
  plan,
  index,
  agentsContent,
  onEditPlan,
}: {
  plan: Partial<EarningPlanEntity>;
  index: number;
  agentsContent?: ReactNode;
  onEditPlan: (
    plan: Partial<EarningPlanEntity>,
    index: number,
    label: string
  ) => void;
}) => {
  const { active, expired } = plan;
  const isLifetime = plan.year === null;
  const amount = formatter.format(centsToDollars(plan?.amount || 0));

  const renderPlanName = () => {
    if (isLifetime) {
      return 'Lifetime Plan';
    } else return `Year ${plan.year} Plan`;
  };

  return (
    <PlanBox
      expired={expired}
      active={active}
      agentsContent={agentsContent}
      rowLeft={
        <Div>
          <P $bold $mb="4">
            {renderPlanName()}
          </P>
          <Disclaimer $mb="4" $color={!active && lightTheme.lightGray}>
            {amount}
          </Disclaimer>
        </Div>
      }
      rowRight={
        <>
          {isLifetime && (
            <Disclaimer $m="0" $right $color={lightTheme.lightGray}>
              Plan that starts when
              <br />
              agent’s Year Plan Ends
            </Disclaimer>
          )}
          <EarningPlanStatus active={active} expired={expired} />
          {!expired && (
            <Icon
              $pointer
              onClick={() => onEditPlan(plan, index, renderPlanName())}
            >
              <EditPenIconDarkGray />
            </Icon>
          )}
        </>
      }
    />
  );
};

export const EarningPlanVOrdersVDrive = ({
  planName,
  amount,
  isEnabled,
  setModalIsOpen,
  agentsContent,
}: {
  planName: string;
  amount: number;
  isEnabled: boolean;
  setModalIsOpen?: Dispatch<SetStateAction<boolean>>;
  agentsContent?: ReactNode;
}) => {
  return (
    <PlanBox
      expired={!isEnabled}
      active={isEnabled}
      agentsContent={agentsContent}
      rowLeft={
        <Div>
          <TextWithTooltipIcon
            bold
            text={planName}
            tooltip={
              !isEnabled &&
              'This service is not enabled for this location in order to define VR plans.'
            }
          />

          {isEnabled && (
            <Disclaimer $mb="4" $color={!isEnabled && lightTheme.gray}>
              {formatter.format(centsToDollars(amount))}
            </Disclaimer>
          )}
        </Div>
      }
      rowRight={
        <>
          <EarningPlanVOrdersVDriveStatus isEnabled={isEnabled} />
          {isEnabled && (
            <Icon $pointer onClick={() => setModalIsOpen?.(true)}>
              <EditPenIconDarkGray />
            </Icon>
          )}
        </>
      }
    />
  );
};

const EarningPlanVOrdersVDriveStatus = ({
  isEnabled,
}: {
  isEnabled: boolean;
}) => {
  return (
    <Pill $expired={!isEnabled}>
      <Disclaimer $m="0" $color={lightTheme.white}>
        {isEnabled ? 'Active' : 'Inactive'}
      </Disclaimer>
    </Pill>
  );
};

export const ServiceAgents = ({
  service,
  label,
  enabled,
  total,
  agents,
  onAdd,
  onDelete,
  onSaveShares,
}: {
  service: LocationAgentServiceKey;
  label: string;
  enabled: boolean;
  total: number;
  agents: LocationAgentShare[];
  onAdd: () => void;
  onDelete: (agentId: number) => void;
  onSaveShares: (
    service: LocationAgentServiceKey,
    amounts: { agentId: number; amount: number }[]
  ) => Promise<boolean> | void;
}) => {
  const [amounts, setAmounts] = useState<Record<number, number>>({});

  // Re-sync local amounts whenever the agents (from the backend) change.
  useEffect(() => {
    const next: Record<number, number> = {};
    agents.forEach((agent) => {
      next[agent.agentId] = agent.amount;
    });
    setAmounts(next);
  }, [agents]);

  const sum = agents.reduce(
    (acc, agent) => acc + (amounts[agent.agentId] ?? 0),
    0
  );
  const matches = sum === total;

  const handleSave = () => {
    if (!matches) {
      return ErrorToast(
        `${label} amounts must sum to ${formatter.format(
          centsToDollars(total)
        )}`
      );
    }
    onSaveShares(
      service,
      agents.map((agent) => ({
        agentId: agent.agentId,
        amount: amounts[agent.agentId] ?? 0,
      }))
    );
  };

  return (
    <>
      <Div $flex $between $alignCenter $m="8px 0 12px 0">
        <P $bold $m="0">
          Agents
        </P>
        {enabled && (
          <Icon $pointer onClick={onAdd}>
            <PlusIcon />
          </Icon>
        )}
      </Div>

      {agents.length === 0 ? (
        <Disclaimer $mb="16" $center $color={lightTheme.lightGray}>
          No agents on this service
        </Disclaimer>
      ) : (
        <>
          {agents.map((agent) => {
            const cents = amounts[agent.agentId] ?? 0;
            const percentage = total
              ? ((cents / total) * 100).toFixed(2)
              : '0.00';
            return (
              <Div
                key={agent.agentId}
                $minHeight="auto"
                $mb="8"
                $flex
                $alignCenter
                $between
                $gap="16px"
              >
                <div>
                  <P $bold $mb="2">
                    {agent.name}
                  </P>
                  <Disclaimer $m="0" $color={lightTheme.lightGray}>
                    {percentage}%
                  </Disclaimer>
                </div>

                <Div $flex $alignCenter $gap="8px">
                  <Div $maxWidth="100px">
                    <AdminDollarInput2
                      value={cents}
                      $small
                      $textCenter
                      onValueChange={(value) =>
                        setAmounts((prev) => ({
                          ...prev,
                          [agent.agentId]: value,
                        }))
                      }
                    />
                  </Div>
                  <Icon $pointer onClick={() => onDelete(agent.agentId)}>
                    <TrashIconRed />
                  </Icon>
                </Div>
              </Div>
            );
          })}

          <Div $flex $between $alignCenter $m="12px 0">
            <Disclaimer $m="0" $color={lightTheme.lightGray}>
              Total {formatter.format(centsToDollars(total))}
            </Disclaimer>
            <Disclaimer
              $m="0"
              $color={matches ? lightTheme.lightGray : lightTheme.primary}
            >
              Sum {formatter.format(centsToDollars(sum))} /{' '}
              {formatter.format(centsToDollars(total))}
            </Disclaimer>
          </Div>

          <Button $selected $w="100%" $small onClick={handleSave}>
            Save
          </Button>
        </>
      )}
    </>
  );
};
